import { FASTAPI_APP_BASE_URL, WORKER_API_URL } from './config';
import { getStoredToken } from '../stores/authStore';
import type {
  ThreadView,
  AppendMessageRequest,
  AppendMessageResponse,
  EditMessageRequest,
  Thread,
  CreateThreadRequest,
  CreateThreadResponse,
  SwitchRevisionRequest,
} from '../types/api';

const API_V1 = '/api/v1';

function authHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  try {
    const token = getStoredToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch {}
  return headers;
}

export const threadService = {
  async startSubchat(conversationRef: string | number, anchorMessageId: string): Promise<Thread> {
    return this.createThread({ conversationRef, kind: 'subchat', anchorMessageId });
  },
  async getByAnchor(conversationRef: string | number, anchorMessageId: string): Promise<Thread | null> {
    const headers = authHeaders();
    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/threads/by-anchor?conversationRef=${encodeURIComponent(String(conversationRef))}&anchorMessageId=${encodeURIComponent(String(anchorMessageId))}`;
    const res = await fetch(url, { headers });
    if (res.ok) {
      try {
        const data = await res.json();
        const th = (data?.thread || data) as any;
        if (th && th.id) return th as Thread;
      } catch {}
    }
    return null;
  },
  async createThread(req: CreateThreadRequest): Promise<Thread> {
    const headers = authHeaders();

    // Always send camelCase body as expected by FastAPI/Pydantic models
    const body = {
      conversationRef: String(req.conversationRef ?? ''),
      kind: req.kind ?? 'subchat',
      anchorMessageId: req.anchorMessageId,
      ...(req.anchorRevisionId ? { anchorRevisionId: req.anchorRevisionId } : {}),
    } as any;

    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/threads`;
    const label = 'primary/camel';
    if (process.env.NODE_ENV !== 'production') {
      try { console.debug('[threadService] createThread attempt', { label, url, bodyPreview: JSON.stringify(body).slice(0, 200) }); } catch {}
    }
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) {
      const data: CreateThreadResponse = await res.json();
      if (data?.thread?.id) return data.thread;
      const maybeThread: any = data as any;
      if (maybeThread?.id) return maybeThread as Thread;
      throw new Error('Unexpected OK without thread');
    }
    if (res.status === 409) {
      try {
        const data = await res.json();
        if (data?.thread?.id) return data.thread as Thread;
        if (data?.id) return data as Thread;
      } catch {}
      try {
        if (req.conversationRef && req.anchorMessageId) {
          const found = await this.getByAnchor(req.conversationRef, req.anchorMessageId);
          if (found?.id) return found;
        }
      } catch {}
      try {
        const threads = await this.listConversationThreads(req.conversationRef);
        const sub = threads.find(t => (t.kind as any) === 'subchat') || threads[0];
        if (sub?.id) return sub;
      } catch {}
      throw new Error('409 without body');
    }
    // Try to decode JSON error for actionable detail
    let errorText = await res.text().catch(() => res.statusText);
    try {
      const parsed = JSON.parse(errorText);
      const detail = parsed?.detail ? JSON.stringify(parsed.detail) : undefined;
      if (detail) errorText = `${res.status} ${detail}`;
    } catch {}
    throw new Error(`${res.status} ${errorText?.slice(0, 200)}`);
  },

  async getThreadView(threadId: string): Promise<ThreadView> {
    const headers = authHeaders();
    const primary = await fetch(`${FASTAPI_APP_BASE_URL}${API_V1}/threads/${threadId}/view`, { headers });
    if (primary.ok) return primary.json();
    const msg = await primary.text().catch(() => primary.statusText);
    throw new Error(msg || 'Failed to fetch thread view');
  },

  async listConversationThreads(conversationRef: string | number): Promise<Thread[]> {
    const headers = authHeaders();
    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/conversations/${conversationRef}/threads`;
    const res = await fetch(url, { headers });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to list threads');
    }
    return res.json();
  },

  async appendMessage(req: AppendMessageRequest): Promise<AppendMessageResponse> {
    const headers = authHeaders();
    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/messages`;
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(req) });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to append message');
    }
    return res.json();
  },

  async editMessage(messageId: string, body: EditMessageRequest): Promise<void> {
    const headers = authHeaders();
    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/messages/${messageId}/edit`;
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to edit message');
    }
  },

  async switchRevision(messageId: string, body: SwitchRevisionRequest): Promise<ThreadView> {
    const headers = authHeaders();
    const url = `${FASTAPI_APP_BASE_URL}${API_V1}/messages/${messageId}/switch_revision`;
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to switch revision');
    }
    return res.json();
  },
};
